import * as Popover from "@radix-ui/react-popover";
import { AppWindow, X } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { formatDuration, parseUtcDate } from "../../utils";
import AppUsagePreview from "../AppUsagePreview";
import { useHoverPin } from "../useHoverPin";
import { appIconUri } from "./appIcon";
import type { ProcessInstance } from "./processGraphModel";
import type { SearchMatch } from "./processSearch";
import { runApps } from "./processSearch";
import { runTitle } from "./processInstanceLabels";

function RunDescription({ description }: { description: string }) {
  const { t } = useTranslation();
  const hover = useHoverPin(450);
  return (
    <Popover.Root open={hover.open} onOpenChange={hover.onOpenChange}>
      <Popover.Anchor asChild>
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={hover.open}
          aria-label={`${t("processPage.runDescription")}: ${description}`}
          onPointerEnter={() => hover.show(true)}
          onPointerLeave={hover.leave}
          onFocus={() => hover.show()}
          onBlur={hover.leave}
          onClick={hover.togglePin}
          className="block w-full rounded-sm text-left text-sm font-normal leading-5 text-text outline-none hover:text-info focus-visible:ring-2 focus-visible:ring-info/30"
        >
          <span className="line-clamp-1">{description}</span>
        </button>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          aria-label={t("processPage.runDescription")}
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={16}
          {...hover.contentProps}
          className="pd-theme z-[120] w-96 max-w-[calc(100vw-2rem)] rounded-xl border border-outline bg-surface p-4 text-text shadow-lift"
        >
          <div className="mb-2 flex items-center justify-between gap-3">
            <h3 className="text-xs font-medium text-text-mid">{t("processPage.runDescription")}</h3>
            <Popover.Close
              aria-label={t("close")}
              className="rounded-md p-1 text-text-dim hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-info/20"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Popover.Close>
          </div>
          <p className="max-h-60 overflow-y-auto whitespace-pre-wrap break-words text-sm leading-relaxed">
            {description}
          </p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default function ProcessRunRow({
  run,
  label,
  match,
  durationLabel,
  interactive = false,
  hidePerson = false,
  action,
}: {
  run: ProcessInstance;
  label: string;
  match?: SearchMatch | null;
  durationLabel?: string;
  interactive?: boolean;
  hidePerson?: boolean;
  action?: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const description = runTitle(run, label);
  const apps = runApps(run);
  const appSeconds: Record<string, number> = {};
  for (const step of run.steps) {
    for (const [name, seconds] of Object.entries(step.appSeconds ?? {})) {
      appSeconds[name] = (appSeconds[name] ?? 0) + seconds;
    }
  }
  const start = parseUtcDate(run.source.traceStart);
  const date = start.toLocaleString(i18n?.resolvedLanguage, {
    month: "short",
    day: "numeric",
    ...(start.getFullYear() !== new Date().getFullYear() && { year: "numeric" }),
    hour: "numeric",
    minute: "2-digit",
  });
  // Other pickers render this component inside a button; only hand-picking gets nested actions.
  const Container = interactive ? "div" : "span";
  return (
    <Container className="flex min-w-0 flex-1 items-start gap-3 text-left">
      <Container className="min-w-0 flex-1">
        {interactive ? (
          <RunDescription description={description} />
        ) : (
          <span
            className="line-clamp-2 text-sm font-normal leading-5 text-text"
            title={description}
          >
            {description}
          </span>
        )}
        <Container className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-mid">
          {!hidePerson && (
            <span className="max-w-40 truncate" title={run.source.userName ?? undefined}>
              {run.source.userName || t("user")}
            </span>
          )}
          {!hidePerson && (
            <span aria-hidden="true" className="text-text-dim">
              ·
            </span>
          )}
          <span className="whitespace-nowrap">{date}</span>
          <span aria-hidden="true" className="text-text-dim">
            ·
          </span>
          <span className="whitespace-nowrap text-text-dim">
            {t("n_steps", { count: run.steps.length })}
          </span>
          {apps.length > 0 &&
            (interactive ? (
              <AppUsagePreview apps={apps} appSeconds={appSeconds} compact />
            ) : (
              <span
                className="inline-flex items-center gap-1"
                title={apps.join(", ")}
                aria-label={apps.join(", ")}
              >
                {apps.slice(0, 3).map(app => {
                  const icon = appIconUri(app);
                  return icon ? (
                    <img key={app} src={icon} alt="" className="h-4 w-4 object-contain" />
                  ) : (
                    <AppWindow key={app} className="h-4 w-4" aria-hidden="true" />
                  );
                })}
                {apps.length > 3 && <span>+{apps.length - 3}</span>}
              </span>
            ))}
        </Container>
        {match && (
          <span className="block truncate text-xs text-info" title={match.value}>
            {t(`processPage.match_${match.field}`, { value: match.value })}
          </span>
        )}
      </Container>
      <Container className="flex w-14 shrink-0 flex-col items-end justify-between gap-1 self-stretch">
        <span className="whitespace-nowrap text-sm tabular-nums text-text-mid">
          {durationLabel ?? formatDuration(run.source.avgDuration)}
        </span>
        {action}
      </Container>
    </Container>
  );
}
